import * as z from "zod";
import { isoDate } from "./common";

/** What a market refresh did. `empty`/`failed`: the previous estimate stands; `pending`: still running. */
export const MARKET_REFRESH_OUTCOMES = ["fresh", "cooldown", "refreshed", "empty", "failed", "pending"] as const;
export type MarketRefreshOutcome = (typeof MARKET_REFRESH_OUTCOMES)[number];

export const MARKET_FAILURE_REASONS = ["unavailable", "empty", "not_found"] as const;

/** The shared market estimate of a pricing-source product, the same in every shop. */
export const MarketEstimate = z.object({
  catalogProductId: z.string(),
  productSource: z.string(),
  productId: z.string(),
  currency: z.string(),
  marketCents: z.number().nullable(),
  dealerBuyCents: z.number().nullable(),
  dealerSellCents: z.number().nullable(),
  /** When the source returned it; null until it first did. A failed refresh never moves it. */
  retrievedAt: isoDate.nullable(),
  /** Retrieved an hour ago or more, or never. */
  stale: z.boolean(),
  lastAttemptAt: isoDate.nullable(),
  /** Set when the latest attempt failed. */
  lastFailureAt: isoDate.nullable(),
  lastFailureReason: z.enum(MARKET_FAILURE_REASONS).nullable(),
});
export type MarketEstimate = z.output<typeof MarketEstimate>;

export const MarketObservation = z.object({
  marketCents: z.number().nullable(),
  dealerBuyCents: z.number().nullable(),
  dealerSellCents: z.number().nullable(),
  currency: z.string(),
  retrievedAt: isoDate,
});
export type MarketObservation = z.output<typeof MarketObservation>;

/** The estimate and its shared history, oldest first. `estimate` is null for a product no source knows. */
export const MarketPrice = z.object({
  estimate: MarketEstimate.nullable(),
  history: z.array(MarketObservation),
});
export type MarketPrice = z.output<typeof MarketPrice>;

export const MarketRefresh = z.object({
  estimate: MarketEstimate,
  outcome: z.enum(MARKET_REFRESH_OUTCOMES),
});
export type MarketRefresh = z.output<typeof MarketRefresh>;
